import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useLifecycleBoards } from '@/modules/LifecycleBoard';
import type LifecycleBoard from '@/types/LifecycleBoard';

type ControlledLifecycleBoardSelectProps = {
  className?: string;
  id?: string;
  label?: string;
  multiple?: boolean;
  name: string;
  required?: boolean;
};

function ControlledLifecycleBoardSelect({
  className,
  id,
  label = translate('LIFECYCLE_BOARD'),
  multiple = false,
  name,
  required = false,
}: ControlledLifecycleBoardSelectProps) {
  const statefulId = useStatefulId(id);
  const { control } = useFormContext();
  const { data: boards }: { data?: LifecycleBoard[] } = useLifecycleBoards();

  const renderValue = React.useCallback(
    (val: string | string[]) => {
      if (!boards) return null;
      const getBoardTitle = (boardId: string) =>
        boards.find((board) => boardId === board.boardId)?.boardTitle;
      const valueLabel = Array.isArray(val)
        ? val.map(getBoardTitle).join(', ')
        : getBoardTitle(val);
      return <div className="max-w-full truncate">{valueLabel}</div>;
    },
    [boards],
  );

  return (
    <FormControl id={statefulId} fullWidth size="small" className={className}>
      <InputLabel id={`${statefulId}-label`} required={required}>
        {label}
      </InputLabel>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            labelId={`${statefulId}-label`}
            id={`${statefulId}-select`}
            label={label}
            multiple={multiple}
            required={required}
            renderValue={renderValue}
            {...field}
          >
            {(boards || []).map(({ boardId, boardTitle }) => (
              <MenuItem value={boardId} key={boardId}>
                {boardTitle}
                {multiple && (
                  <Checkbox
                    className="ml-auto"
                    checked={field.value.includes(boardId)}
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FormControl>
  );
}

export default ControlledLifecycleBoardSelect;
